import axios from 'axios';
import { onAccountingChanged } from './accounting/accountingConnectService';
angular.module('leanlaw.core').service('accountingService', ['$q', 'global', 'leanLawApi', 'userService', 'leanLawAuth', function ($q, global, leanLawApi, userService, leanLawAuth) {
  var domain = leanLawAuth.domain + '/v1';
  var _clientsPromise = null;
  var _syncPromise = null;
  var _accountsPromises = {};
  var _trustPromise = null;
  var _childAccountsPromises = {};
  var _state = {
    connected: false,
    clients: []
  };
  var getAccounting = global.ready.then(() => {
    return leanLawApi.getAccounting().then(function (status) {
      _state.connected = status.connected;
      return _state.connected;
    });
  });
  function verify(prompt) {
    return userService.me().then(me => {
      if (!me.company.integration.quickbooks) {
        return $q.reject({
          enabled: false
        });
      }
      return leanLawApi.getAccounting().then(status => {
        if (!status.connected) {
          if (prompt) {
            var d = $q.defer();
            // TODO this doesn't belong in core, remove!
            bootbox.alert("You are disconnected from QuickBooks. Go to Settings to reconnect...", function () {
              d.reject(status);
            });
            return d.promise;
          } else {
            return $q.reject(status);
          }
        }
        return status;
      });
    });
  }
  function getClients(refresh) {
    return userService.me().then(me => {
      if (!me.company.integration.quickbooks) {
        return $q.reject();
      }
      if (!_clientsPromise || refresh) {
        _clientsPromise = getAccounting.then(connected => {
          if (connected) {
            // includeUnmatched: false opts into the fast, linked-ids-only path (sc-45975) --
            // the backend defaults this to true (the old full-scan behavior) so a request that
            // omits it can't silently lose unmatched QuickBooks customers.
            return leanLawApi.listAccountingClients({
              scope: 'all',
              $queryParameters: {
                includeUnmatched: false
              }
            }).then(list => {
              _state.clients = list.clients;
              return list.clients;
            });
          }
          return $q.reject();
        });
      }
      return _clientsPromise;
    });
  }

  // Bounded name/parent search for the QuickBooks Connector picker (sc-46822) -- finds ONE
  // customer to link instead of loading accountingService.state().clients (the whole-firm list
  // above). Deliberately bypasses leanLawApi.js: new endpoint calls go directly via axios rather
  // than growing that generated-style client (see .claude/rules/state-and-data.md), matching
  // childAccounts below. Does not touch _clientsPromise/_state.clients -- that cache is unrelated
  // to this search and stays exactly as-is for every other consumer (validate/validateClient,
  // matter view, etc.).
  function searchClients(term, parentId) {
    return axios.get(`${domain}/accounting/clients/search`, {
      params: {
        q: term,
        parentId
      }
    }).then(response => response.data.clients);
  }

  // Full, unfiltered QuickBooks customer roster (matched AND unmatched) for the "Import from
  // QuickBooks" screen (import.ctrl.js) -- the only consumer that needs customers with no
  // LeanLaw match yet. Deliberately bypasses the getClients()/_clientsPromise cache above: that
  // cache backs the fast, linked-ids-only default path used by validate/validateClient
  // everywhere else in the app, and must stay untouched by this rarer, slower call. See sc-45975.
  function getImportableClients() {
    return axios.get(`${domain}/accounting/clients`, {
      params: {
        scope: 'all',
        includeUnmatched: true
      }
    }).then(response => response.data.clients);
  }
  function validate(matter) {
    if (matter.client.accounting) {
      var client = find(_state.clients, matter.client.accounting.id);
      matter.client.accounting.gone = !client;
      if (matter.accounting) {
        matter.accounting.gone = !(client && client.clients && find(client.clients, matter.accounting.id));
      }
    } else if (matter.accounting) {
      matter.accounting.gone = !find(_state.clients, matter.accounting.id);
    }
  }
  function validateClient(client) {
    var clientAccounting = client.client ? client.client.accounting : client.accounting;
    if (clientAccounting) {
      var qbClient = find(_state.clients, clientAccounting.id);
      clientAccounting.gone = !qbClient;
    }
    client.matters.forEach(matter => {
      var matterAccounting = matter.matter ? matter.matter.accounting : matter.accounting;
      if (matterAccounting) {
        matterAccounting.gone = !(qbClient && qbClient.clients && find(qbClient.clients, matterAccounting.id));
      }
    });
  }
  function find(list, id) {
    return _.find(list, c => c.id == id);
  }
  function listAccounts(type) {
    var key = type || '';
    if (!_accountsPromises[key]) {
      _accountsPromises[key] = leanLawApi.listAccountingAccounts({
        type: type
      }).then(function (list) {
        return list;
      });
    }
    return _accountsPromises[key];
  }
  function getTrust() {
    if (!_trustPromise) {
      _trustPromise = leanLawApi.getAccountingTrust().then(function (list) {
        return list;
      });
    }
    return _trustPromise;
  }

  // All children (active and inactive) of one parent account. Memoized per parentId so reopening
  // the trust dialog in the same session issues no further reads. A distinct endpoint from GET
  // accounting/accounts (different response shape: flat list here vs the bucketed type scan
  // there). Calls axios directly since leanLawApi is being phased out -- no new code adds to it.
  function childAccounts(parentId) {
    if (!_childAccountsPromises[parentId]) {
      _childAccountsPromises[parentId] = axios.get(domain + '/accounting/accounts/filtered', {
        params: {
          parentId: parentId
        }
      }).then(function (response) {
        return response.data;
      });
    }
    return _childAccountsPromises[parentId];
  }
  function createAccount(account) {
    return leanLawApi.createAccountingAccount({
      body: account
    }).then(function (created) {
      // createAccount is the only mutation choke point a trust save reaches (clearCache below is
      // not), so patch the affected parent's cached child list in place rather than dropping it.
      var parentId = account.parent && account.parent.id;
      if (parentId && _childAccountsPromises[parentId]) {
        _childAccountsPromises[parentId] = _childAccountsPromises[parentId].then(function (list) {
          list.push(created);
          return list;
        });
      }
      return created;
    });
  }
  function getAccount(id, transactions) {
    var request = {
      id
    };
    if (transactions) {
      request.include = 'transactions';
    }
    return leanLawApi.getAccountingAccount(request);
  }
  function listServices() {
    return leanLawApi.listAccountingServices().then(function (list) {
      return list;
    });
  }
  function listTaxcodes() {
    return leanLawApi.listTaxcodes().then(function (list) {
      return list.taxcodes;
    });
  }
  function listTerms() {
    return leanLawApi.listAccountingTerms().then(function (list) {
      return list.terms;
    });
  }
  function updateMatter(id, name) {
    return leanLawApi.updateMatterAccounting({
      id: id,
      body: {
        accounting: {
          name
        }
      }
    }).then(clearCache);
  }
  function updateClient(id, name, billClient) {
    return leanLawApi.updateClientAccounting({
      id,
      body: {
        accounting: {
          name
        },
        billClient
      }
    }).then(clearCache);
  }

  // Keeps the cached QuickBooks customer list honest when a connect runs outside
  // this service (TrustDeposit.vue via accountingConnectService).
  onAccountingChanged(() => clearCache());
  function clearCache(result) {
    _clientsPromise = null;
    _state.clients = [];
    return result;
  }
  function createService(service) {
    return leanLawApi.createAccountingService({
      body: service
    });
  }
  function getClient(id) {
    return leanLawApi.getAccountingClient({
      id
    });
  }
  function combineQuickBooksAccounts(accounts, types) {
    const result = [];
    types.forEach(type => {
      accounts[type].forEach(a => {
        a.label = `${a.name} (${a.type})`;
        result.push(a);
      });
    });
    return result;
  }
  return {
    state: _state,
    clients: getClients,
    searchClients,
    getImportableClients,
    getClient,
    accounts: listAccounts,
    childAccounts,
    createAccount,
    getAccount,
    trust: getTrust,
    taxcodes: listTaxcodes,
    terms: listTerms,
    updateMatter,
    updateClient,
    services: listServices,
    createService: createService,
    combineQuickBooksAccounts,
    verify,
    validate,
    validateClient,
    clearCache,
    refresh() {
      _clientsPromise = null;
      _state.clients = [];
      return getClients();
    }
  };
}]);